import axios from 'axios'
import React, { useContext, useEffect, useState } from 'react'
import { MdRestaurant } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { HeaderActions, IFoodTable } from 'safe'
import Loading from '../../components/Display/Loading'
import Modal from '../../components/Display/Modal'
import { ThemeContext } from '../../context/ThemeContext'
import useSnackBars from '../../hooks/SnackbarConsumerHook'
import { RootState } from '../../redux/store'
import { FoodOrderForm, IFormFoodOrder } from './FoodOrderForm'
import FoodTableCard from './FoodTableCard'
import { FoodTableForm, IFormFoodTable } from './FoodTableForm'

const server = process.env.REACT_APP_SERVER_URL

export const FoodTable: React.FC<FoodTableProps> = ({ setHeaderActions }) => {
  const [footTables, setFootTables] = useState<IFoodTable[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [isLoadingGet, setIsLoadingGet] = useState<boolean>(false)

  const [createModal, setCreateModal] = useState<boolean>(false)

  const [selectedTable, setSelectedTable] = useState<IFoodTable | null>(null)

  const [useType, setuseType] = useState<'create' | 'edit' | 'watch'>('create')

  const { addAlert } = useSnackBars()
  const { theme } = useContext(ThemeContext)

  const [selectedTableToCreateFoodOrder, setSelectedTableToCreateFoodOrder] =
    useState<IFoodTable | null>(null)

  const { user } = useSelector((state: RootState) => state.auth)
  const userPlan = user?.['custom:user_plan']
  const isEmprendedorUser =
    userPlan === 'emprendedor' || userPlan === 'estudiantil'

  const history = useHistory()

  const onGetFootTables = async () => {
    try {
      setIsLoadingGet(true)
      const response = await axios.get<{ tables: IFoodTable[] }>(
        `${server}/food-table`
      )
      setFootTables(response.data.tables)
      setIsLoadingGet(false)
    } catch (error) {
      console.log(error)
      setIsLoadingGet(false)
    }
  }

  const onSubmit = async (values: IFormFoodTable): Promise<void> => {
    if (useType === 'create') {
      try {
        setIsLoading(true)
        const response = await axios.post<IFormFoodTable>(
          `${server}/food-table`,
          values
        )
        await onGetFootTables()
        addAlert({
          message: 'Mesa creada correctamente',
          status: 'success',
        })
        setIsLoading(false)
        onCloseModal()
      } catch (error: any) {
        addAlert({
          message: error?.response?.data?.message || 'Error al crear la mesa',
          status: 'error',
        })
        setIsLoading(false)
      }
    } else if (useType === 'edit') {
      try {
        setIsLoading(true)
        const response = await axios.patch<IFormFoodTable>(
          `${server}/food-table?id=${selectedTable?.id}`,
          { ...values, id: selectedTable?.id }
        )
        await onGetFootTables()
        addAlert({
          message: 'Mesa editada correctamente',
          status: 'success',
        })
        setIsLoading(false)
        onCloseModal()
      } catch (error: any) {
        addAlert({
          message: error?.response?.data?.message || 'Error al editar la mesa',
          status: 'error',
        })
      }
    }
  }

  const onFilterHeader = (obj: { addAction: () => void }) => {
    if (
      user['custom:user_role'] === 'master' ||
      user['custom:user_role'] === 'general_manager' ||
      user['custom:user_role'] === 'admin'
    ) {
      return obj
    }
    return {}
  }

  useEffect(() => {
    // No cargar datos ni mostrar acciones si es plan emprendedor
    if (!isEmprendedorUser) {
      onGetFootTables()

      setHeaderActions(
        onFilterHeader({
          addAction: () => setCreateModal(true),
        })
      )
    } else {
      setHeaderActions({})
    }

    return () => {
      setHeaderActions(null)
    }
  }, [isEmprendedorUser])

  const onDelete = async (id: string) => {
    try {
      addAlert({
        message: 'Eliminando mesa...',
        status: 'loading',
      })
      await axios.delete(`${server}/food-table?id=${id}`)
      await onGetFootTables()
      addAlert({
        message: 'Mesa eliminada correctamente',
        status: 'success',
      })
    } catch (error: any) {
      if (
        error?.response?.data?.message === 'The food item has associated data'
      ) {
        addAlert({
          message: 'La mesa tiene datos asociados',
          status: 'error',
        })
        return
      }
      addAlert({
        message: 'Error al eliminar mesa',
        status: 'error',
      })
    }
  }

  const onCloseModal = () => {
    setCreateModal(false)
    setuseType('create')
    setSelectedTable(null)
  }

  const onWatch = async (table: IFoodTable) => {
    setuseType('watch')
    setSelectedTable(table)
    setCreateModal(true)
  }

  const onEdit = async (table: IFoodTable) => {
    setuseType('edit')
    setSelectedTable(table)
    setCreateModal(true)
  }

  const onCreateOrder = async (table: IFoodTable) => {
    setSelectedTableToCreateFoodOrder(table)

    console.log(table)
  }

  const onSubmitFoodOrder = async (data: IFormFoodOrder) => {
    const body = {
      idFoodTable: selectedTableToCreateFoodOrder?.id,
      type: data.type,
      idNevulaUser: user?.sub,
      observations: data.observations,
    }

    try {
      setIsLoading(true)
      const response: any = await axios.post<IFormFoodOrder>(
        `${server}/food-order`,
        body
      )
      await onGetFootTables()
      addAlert({
        message: 'Comanda creada correctamente',
        status: 'success',
      })
      setSelectedTableToCreateFoodOrder(null)
      history.push(
        `/dashboard/restaurantes/comandas?foodOrderId=${response.data.id}`
      )
    } catch (error: any) {
      if (
        error?.response?.data?.message ===
        'There is already an active order for this food table'
      ) {
        addAlert({
          message: 'Ya existe una comanda activa para esta mesa',
          status: 'error',
        })
      } else {
        addAlert({
          message: 'Error al crear comanda',
          status: 'error',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoadingGet) {
    return <Loading />
  }

  // Mostrar mensaje de upgrade si es plan emprendedor
  if (isEmprendedorUser) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-4">
        <div
          className="p-8 rounded-lg shadow-lg max-w-2xl text-center"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="mb-6">
            <MdRestaurant
              className="mx-auto mb-4"
              size={64}
              style={{ color: '#507CDC' }}
            />
          </div>
          <h2 className="text-2xl font-bold mb-4 dark:text-white">
            Funcionalidad Premium
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Para gestionar mesas debes mejorar tu plan a{' '}
            <strong className="text-primary">"PYME"</strong>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            El plan Corporativo incluye gestión completa de mesas con control de
            disponibilidad, estados en tiempo real y mucho más.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      {footTables.length === 0 && !isLoadingGet && (
        <div className="flex justify-center items-center h-[60vh]">
          <h1 className="text-2xl font-bold">No hay mesas disponibles</h1>
        </div>
      )}

      {footTables.length > 0 && (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 px-1">
          {footTables.map((table) => (
            <FoodTableCard
              table={table}
              onDelete={onDelete}
              onWatch={() => onWatch(table)}
              onEdit={() => onEdit(table)}
              onCreateOrder={() => onCreateOrder(table)}
            />
          ))}
        </div>
      )}
      <Modal
        xlClasses="w-[700px]"
        display={createModal}
        onClose={() => onCloseModal()}
      >
        <FoodTableForm
          onSubmit={onSubmit}
          onClose={() => onCloseModal()}
          useType={useType}
          isLoading={isLoading}
          defaultValues={selectedTable!}
        />
      </Modal>

      <Modal
        xlClasses="w-[700px]"
        display={!!selectedTableToCreateFoodOrder}
        onClose={() => setSelectedTableToCreateFoodOrder(null)}
      >
        <FoodOrderForm
          tables={footTables.map((table) => ({
            value: table.id.toString(),
            text: table.name,
          }))}
          onSubmit={onSubmitFoodOrder}
          onClose={() => setSelectedTableToCreateFoodOrder(null)}
          useType={useType}
          isLoading={isLoading}
          defaultValues={
            {
              ...selectedTableToCreateFoodOrder,
              idFoodTable: selectedTableToCreateFoodOrder?.id.toString(),
            }!
          }
          isFromFoodTable={true}
        />
      </Modal>
    </div>
  )
}

interface FoodTableProps {
  setHeaderActions: React.Dispatch<React.SetStateAction<HeaderActions | null>>
}
